import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getStatusOptions } from '../../notification/services/useStatusQuery'
import { messageApi } from '../api/messageApi'
import { getConversationMessagesOptions } from './useConversationMessagesQuery'
import { getConversationsPreviewOptions } from './useConversationsPreviewQuery'
import { getConversationOptions } from './useSingleConversationQuery'

export const useMarkConversationAsUnreadMutation = (conversationId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      let messagesData = queryClient.getQueryData(getConversationMessagesOptions(conversationId).queryKey)

      // In the case of messagesData wouldn't be in the cache, we fetch it
      if (!messagesData || !messagesData.pages || messagesData.pages.length === 0)
        messagesData = await queryClient.fetchInfiniteQuery(getConversationMessagesOptions(conversationId))

      // first [0] to get the first page, last [0] to get the last message
      const lastMessageId = messagesData.pages[0][0].id

      return messageApi.markConversationAsUnread(lastMessageId)
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: getConversationOptions(conversationId).queryKey })
      await queryClient.cancelQueries({ queryKey: getConversationsPreviewOptions().queryKey })
      await queryClient.cancelQueries({ queryKey: getConversationsPreviewOptions({ unread: true }).queryKey })

      // Snapshot the previous values
      const previousConversation = queryClient.getQueryData(getConversationOptions(conversationId).queryKey)
      const previousConversations = queryClient.getQueryData(getConversationsPreviewOptions().queryKey)
      const previousUnreadConversations = queryClient.getQueryData(
        getConversationsPreviewOptions({ unread: true }).queryKey,
      )

      // Optimistically update the cache
      queryClient.setQueryData(
        getConversationOptions(conversationId).queryKey,
        (data) =>
          data && {
            ...data,
            hasUnreadMessages: true,
          },
      )

      // Optimistically update the all conversations list
      queryClient.setQueryData(
        getConversationsPreviewOptions().queryKey,
        (data) =>
          data && {
            ...data,
            pages: data.pages.map((page) =>
              page.map((conversation) =>
                conversation.id === conversationId ? { ...conversation, hasUnreadMessages: true } : conversation,
              ),
            ),
          },
      )

      // Return a context object with the snapshotted values
      return { previousConversation, previousConversations, previousUnreadConversations }
    },
    onError: (_err, _var, context) => {
      if (context?.previousConversation)
        queryClient.setQueryData(getConversationOptions(conversationId).queryKey, context.previousConversation)

      if (context?.previousConversations)
        queryClient.setQueryData(getConversationsPreviewOptions().queryKey, context.previousConversations)

      if (context?.previousUnreadConversations) {
        queryClient.setQueryData(
          getConversationsPreviewOptions({ unread: true }).queryKey,
          context.previousUnreadConversations,
        )
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getConversationOptions(conversationId).queryKey })
      queryClient.invalidateQueries({ queryKey: getConversationsPreviewOptions().queryKey })
      queryClient.invalidateQueries({ queryKey: getConversationsPreviewOptions({ unread: true }).queryKey })
      queryClient.invalidateQueries({ queryKey: getStatusOptions().queryKey })
    },
  })
}
